import { useEffect, useState } from "react";
import { Mail, ArrowUp } from "lucide-react";
import { toast, Toaster } from "sonner";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Message sent — Jacob will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send right now — email jacob.tobery2@gmail.com instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-[#00E5FF]";

  return (
    <div data-testid="contact-form-wrap" className="mt-12 grid grid-cols-1 gap-8 border-t border-white/10 pt-10 md:grid-cols-12">
      <div className="md:col-span-5">
        <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#00E5FF]">Message me</p>
        <h3 className="mt-4 font-display text-3xl font-black uppercase tracking-tight md:text-4xl">
          Drop a line,
          <br />
          <span className="text-stroke">I&apos;ll reply</span>
        </h3>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-400 md:text-base">
          Ideas, internships, collaborations — this form lands straight in my inbox.
        </p>
      </div>
      <form data-testid="contact-form" onSubmit={onSubmit} className="flex flex-col gap-4 md:col-span-7">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input data-testid="contact-name-input" name="name" required maxLength={80} placeholder="Your name" value={form.name} onChange={onChange} className={inputCls} aria-label="Your name" />
          <input data-testid="contact-email-input" name="email" type="email" required placeholder="Your email" value={form.email} onChange={onChange} className={inputCls} aria-label="Your email" />
        </div>
        <textarea data-testid="contact-message-input" name="message" required maxLength={2000} rows={5} placeholder="What's on your mind?" value={form.message} onChange={onChange} className={`${inputCls} resize-none`} aria-label="Your message" />
        <div className="flex items-center justify-end gap-4">
          <button data-testid="contact-submit-button" type="submit" disabled={sending} className="rounded-full bg-[#00E5FF] px-7 py-3 font-jbmono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white disabled:opacity-50">
            {sending ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
    </div>
  );
}

const SOCIALS = [
  { icon: Mail, label: "Email", href: "mailto:jacob.tobery2@gmail.com" },
];

function usePacificTime() {
  const [time, setTime] = useState("");
  useEffect(() => {
    const tick = () =>
      setTime(
        new Date().toLocaleTimeString("en-US", {
          timeZone: "America/Los_Angeles",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

export default function Contact() {
  const time = usePacificTime();

  return (
    <footer id="contact" data-testid="contact-section" className="relative border-t border-white/10 bg-[#0a0a0a]">
      <div className="mx-auto max-w-[1400px] px-6 pb-10 pt-16 md:px-10 md:pt-24">
        <FadeUp>
          <p className="mb-6 font-jbmono text-xs uppercase tracking-[0.3em] text-[#00E5FF]">
            Get in touch
          </p>
          <a
            href="mailto:jacob.tobery2@gmail.com"
            data-testid="contact-email-link"
            className="group block"
          >
            <span className="block overflow-hidden">
              <span className="text-stroke font-display block text-[clamp(3.4rem,13vw,11rem)] font-black uppercase leading-[0.9] tracking-[-0.02em] transition-colors duration-500 group-hover:text-[#00E5FF] group-hover:[-webkit-text-stroke:0px]">
                Let&apos;s talk
              </span>
            </span>
          </a>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="mailto:jacob.tobery2@gmail.com"
              data-testid="contact-email-button"
              className="rounded-full bg-[#00E5FF] px-7 py-3.5 font-jbmono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
            >
              jacob.tobery2@gmail.com
            </a>
            <span className="font-jbmono text-[11px] uppercase tracking-[0.25em] text-zinc-500" data-testid="contact-availability">
              Open to internships &amp; collaborations
            </span>
          </div>
        </FadeUp>

        <ContactForm />
        <Toaster theme="dark" position="bottom-right" />

        <div className="mt-14 flex flex-col gap-8 border-t border-white/10 pt-8 md:mt-16 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            {SOCIALS.map(({ icon: Icon, label, href }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                data-testid={`contact-social-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-full border border-white/10 p-3 text-zinc-400 transition-colors duration-300 hover:border-[#00E5FF] hover:text-[#00E5FF]"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-6 font-jbmono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            <span data-testid="footer-local-time">Tri-Cities, WA — {time || "00:00:00"} PT</span>
            <span>©2026 Jacob Tobery</span>
          </div>

          <button
            data-testid="footer-back-to-top"
            onClick={() => {
              if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.6 });
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex w-fit items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 font-jbmono text-[11px] uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-white/40 hover:text-white"
          >
            Back to top <ArrowUp size={13} />
          </button>
        </div>
      </div>
    </footer>
  );
}
